// Pure read helpers for one agent-trace row. No React, no fetch — the formatting
// decisions that are easy to get subtly wrong live here so they can be tested
// directly.

import type { TraceStepDTO } from "@recruiter/shared/types";

/** Render any jsonb value as readable text: a string as itself, anything else as
 *  pretty JSON, and an unserializable value as its `String()` form rather than
 *  throwing inside a render. */
export function asText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** A call's wall-clock duration from its latency in ms: a dash when not captured,
 *  "847ms" under a second, "1.2s" / "12s" above. A negative value is treated as
 *  unknown rather than printed. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || ms < 0) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  return `${s.toFixed(s < 10 ? 1 : 0)}s`;
}

/** Total duration of a run: the sum of its captured per-call latencies. A null
 *  latency counts as 0, so a run whose calls were all un-timed sums to "0ms" —
 *  which is honest ("nothing was measured"), not a claim that it was instant. */
export function runDurationMs(steps: readonly TraceStepDTO[]): number {
  return steps.reduce((sum, s) => sum + (s.latency_ms ?? 0), 0);
}

/** Display view of ONE transcript item.
 *
 *  Input messages are a mix of chat-style `{role, content}` messages AND
 *  Responses-API items the agent tool-loops thread back in: `function_call` (the
 *  model's own tool call, echoed to anchor the next turn) and
 *  `function_call_output` (the tool's RESULT, e.g. SCOUT's gateway search hits).
 *  Those item types carry no `role`/`content`, so without this branch they render
 *  as blank rows labelled "?" and the most interesting half of a SCOUT trace looks
 *  empty. */
export function traceMessageView(message: unknown): { role: string; body: string } {
  const rec = (message ?? {}) as Record<string, unknown>;
  const type = asText(rec.type);
  if (type === "function_call") {
    const name = asText(rec.name) || "tool";
    return { role: "tool call", body: `${name}(${asText(rec.arguments)})` };
  }
  if (type === "function_call_output") {
    return { role: "tool result", body: asText(rec.output) };
  }
  return { role: asText(rec.role) || "?", body: asText(rec.content) };
}
